import { formatDistance } from 'date-fns';
import { Map } from 'immutable';

import { FILTERS_MAP } from './ducks/filters/constants';
import * as urlQueryString from './urlQueryString';
import { SIXTEEN_DAYS } from './constants';

export const testCasesSort = (testCases, sortFieldName, direction) => {
  if (direction) {
    return testCases.sort((prev, curr) => prev[sortFieldName] < curr[sortFieldName]);
  }
  return testCases.sort((prev, curr) => prev[sortFieldName] > curr[sortFieldName]);
};

export const formatDuration = (selectedDateFilter) => {
  if (selectedDateFilter.get('type') === 'absolute') {
    const start = selectedDateFilter.get('value').startDate;
    const end = selectedDateFilter.get('value').endDate;
    return formatDistance(start, end);
  }

  return formatDistance(
    new Date(Date.now() - (selectedDateFilter.get('value') * 1000)),
    new Date(),
  );
};

export const getFiltersFromQueryParams = (allQueryParams, defaultFilters = {}) => (
  Object.keys(allQueryParams)
    .reduce((ac, key) => ({
      ...ac,
      ...(FILTERS_MAP.has(key) && { [key]: allQueryParams[key] }),
    }), defaultFilters)
);

export const getDateFilterFromQueryParams = (allQueryParams) => {
  let dateValue = JSON.parse(allQueryParams.dateFilter);
  if (dateValue.type === 'absolute') {
    dateValue = {
      ...dateValue,
      value: {
        startDate: new Date(dateValue.value.startDate),
        endDate: new Date(dateValue.value.endDate),
      },
    };
  }
  return new Map(dateValue);
};

export const selectedDateTransformer = (location, payload) => (
  urlQueryString.set(location, { dateFilter: JSON.stringify(payload) })
);

export const removeFilterTransformer = (location, payload) => (
  urlQueryString.remove(location, payload.name, payload.value)
);

export const selectFilterTransformer = (location, payload) => (
  urlQueryString.set(location, { [payload.name]: payload.items.toJSON() })
);

export const searchValueTransformer = (location, payload) => (
  urlQueryString.set(location, { query: payload })
);

export const clearSelectedFilterTransformer = (location) => {
  const allQueryParams = urlQueryString.getAll(location);
  return Object.keys(allQueryParams)
    .reduce((ac, param) => ({
      ...ac,
      ...(!FILTERS_MAP.get(param) && { [param]: allQueryParams[param] }),
    }), {});
};

export const notifyNewError = (nextErrors, prevErrors, callback) => {
  if (nextErrors.equals(prevErrors)) {
    return;
  }
  nextErrors.forEach((value, key) => {
    if (!value || prevErrors.get(key) === value) return;
    callback(`There was an error: ${value}.`);
  });
};

export const getStartEndDate = (dateFilter, base = new Date()) => {
  const type = dateFilter.get('type');
  const value = dateFilter.get('value');
  const startDate = type === 'absolute' ? new Date(value.startDate.getTime()) : new Date(base - (value * 1000));
  const endDate = type === 'absolute' ? new Date(value.endDate.getTime()) : new Date(base);
  return {
    startDate,
    endDate,
  };
};

export class NoDataError extends Error {}

export const calculatePercentage = (partialValue, totalValue, decimal = 2) => (
  totalValue ? (100 * (partialValue || 0)) / totalValue : 0
).toFixed(decimal);

export const formatLabel = (item) => (item || 0);

export const formatComparisonLabel = (item, date) => {
  const pastData = formatLabel(item.get('previousValue'));
  const currentData = formatLabel(item.get('value'));
  if (typeof currentData === 'string') return null;

  // right now we only store ~30 days of test data.  If the selected time range is greater
  // than 16 days we will not have accurate data to compare.
  if (date.get('value') >= SIXTEEN_DAYS) {
    return '--';
  }

  if (currentData === pastData) {
    return `vs. ${pastData} (0%)`;
  }

  //  if the prior number of tests ran is equal to 0 it's not possible to
  //  accurately calculate the percent
  if (pastData === 0) {
    return `vs. ${pastData} (--%)`;
  }

  const percentage = (((currentData - pastData) / pastData) * 100).toFixed(1);
  return `vs. ${pastData} (${percentage}%)`;
};
